import { useCallback, useState } from "react";
import { useApiForm } from "@/components/hooks/useApiForm";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Alert } from "@/components/ui/Alert";
import { ClipboardDocumentIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

type ApiKeyFormData = {
  name: string;
};

export default function ApiKeyForm({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (value: boolean) => any;
}) {
  const [name, setName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { submit, isLoading } = useApiForm<ApiKeyFormData>({
    url: "/web_api/api_keys",
    method: "POST",
    mutateKeys: ["/web_api/api_keys"],
    onSuccess: (data) => {
      if (data.api_key) {
        setCreatedKey(data.api_key.key);
      }
    },
  });

  const onSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      submit({ name: name.trim() });
    },
    [name, submit],
  );

  const copyKey = useCallback(() => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [createdKey]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Reset form after closing animation
    setTimeout(() => {
      setName("");
      setCreatedKey(null);
      setCopied(false);
    }, 300);
  }, [setOpen]);

  return (
    <Dialog open={open} onClose={handleClose}>
      {!createdKey ? (
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for programmatic access. The key will be displayed once after
              creation.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">API Key Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Production API Key"
                  required
                />
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading ? "Creating..." : "Create API Key"}
            </Button>
          </DialogFooter>
        </form>
      ) : (
        <>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>
              Your API key has been created. Make sure to copy it now - you will not be able to see
              it again!
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="grid gap-4">
              <Alert>
                <div className="space-y-2">
                  <code className="block text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                    {createdKey}
                  </code>
                  <Button variant="ghost" size="sm" onClick={copyKey} className="w-full">
                    {copied ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                        Copy API Key
                      </>
                    )}
                  </Button>
                </div>
              </Alert>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
